// client/src/Dashboard.js
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
console.log('API:', API);

// ---- Recommended logic helpers ----
const RECO_TARGET        = 7;
const RECO_UPCOMING_DAYS = 4;   // “3–4 days”
const RECO_SHARED_DAYS   = 21;  // pinned categories window;

// Days until a local YYYY-MM-DD date
const daysUntil = (iso) => {
  if (!iso) return Infinity;
  const today = new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d = new Date(iso + 'T00:00:00');
  return Math.floor((d - t0) / (24 * 60 * 60 * 1000));
};

// Return a CSS class for urgency tint
const urgencyClass = (dateLike) => {
  const iso = toISODateSafe(dateLike);
  if (!iso) return '';
  const n = daysUntil(iso);
  if (n < 0) return 'u-past'; // optional gray for past
  if (n <= 7) return 'u-1w';  // ≤ 1 week
  if (n <= 14) return 'u-2w'; // ≤ 2 weeks
  return 'u-later';           // > 2 weeks
};

function buildScrapedPayload(item) {
  const iso =
    toISODateSafe(item.date || item.dateText || item.text || item.event) || '';
  const title = (item.event || item.title || '')
    .toLowerCase()
    .slice(0, 80);
  return {
    key: `scr|${iso}|${title}`,
    event: item.event || item.title || '',
    category: (item.category || 'other').toLowerCase(),
    dateISO: iso,
    source: 'scraped'
  };
}

function buildPersonalPayload(email, evt) {
  const iso = ymdFromIso(evt.start);
  return {
    email,
    key: `me|${evt._id}`,
    event: evt.title || '',
    category: (evt.category || 'personal').toLowerCase(),
    dateISO: iso,
    source: 'personal'
  };
}

function getISOFromItem(it) {
  return toISODateSafe(it.date || it.dateText || it.text || it.event);
}

/**
 * Compute Recommended Deadlines (YOUR SPEC)
 */
function computeRecommendedDeadlines({
  allItems = [],
  pinnedCats = new Set(),
  excludeKeys = new Set(),
  target = RECO_TARGET
}) {
  const today = startOfDay(new Date());
  const day = (n) => addDays(today, n);

  const keyFor = (it) =>
    `scr|${getISOFromItem(it) || ''}|${(it.event || it.title || '')
      .toLowerCase()
      .slice(0, 80)}`;

  // Base future pool (exclude currently pinned scraped)
  const baseFuture = allItems.filter((it) => {
    const iso = getISOFromItem(it);
    if (!iso) return false;
    const d = new Date(iso + 'T00:00:00');
    if (d < today) return false;
    return !excludeKeys.has(keyFor(it));
  });

  const byDateAsc = (a, b) => {
    const da = new Date(getISOFromItem(a) + 'T00:00:00');
    const db = new Date(getISOFromItem(b) + 'T00:00:00');
    return da - db;
  };

  const chosen = [];
  const seen = new Set();

  const addList = (list) => {
    for (const it of list) {
      const k = keyFor(it);
      if (seen.has(k)) continue;
      seen.add(k);
      chosen.push(it);
      if (chosen.length >= target) break;
    }
  };

  // 1) ≤ 4 days out
  const soon = baseFuture
    .filter((it) => {
      const d = new Date(getISOFromItem(it) + 'T00:00:00');
      return d >= today && d < day(RECO_UPCOMING_DAYS + 1);
    })
    .sort(byDateAsc);
  addList(soon);
  if (chosen.length >= target) return chosen.sort(byDateAsc);

  // 2) Pinned categories within 21 days
  if (pinnedCats.size) {
    const shared = baseFuture
      .filter((it) => {
        const cat = (it.category || 'other').toLowerCase();
        const d = new Date(getISOFromItem(it) + 'T00:00:00');
        return (
          pinnedCats.has(cat) && d >= today && d < day(RECO_SHARED_DAYS + 1)
        );
      })
      .sort(byDateAsc);
    addList(shared);
    if (chosen.length >= target) return chosen.sort(byDateAsc);
  }

  // 3) Category ladder...
  const ladderOrder = ['add/drop', 'financial-aid', 'registration', 'academic'];
  let offset = 7;
  let safety = 0;
  const MAX_STEPS = 365;

  while (chosen.length < target && safety < MAX_STEPS) {
    let addedThisRound = false;
    for (const cat of ladderOrder) {
      const candidate = baseFuture
        .filter((it) => {
          const c = (it.category || 'other').toLowerCase();
          if (c !== cat) return false;
          const d = new Date(getISOFromItem(it) + 'T00:00:00');
          return d >= day(offset);
        })
        .sort(byDateAsc)
        .find((it) => !seen.has(keyFor(it)));
      if (candidate) {
        seen.add(keyFor(candidate));
        chosen.push(candidate);
        addedThisRound = true;
        if (chosen.length >= target) break;
      }
    }
    if (!addedThisRound) offset += 1;
    safety += 1;
  }

  if (chosen.length < target) {
    const filler = baseFuture
      .filter((it) => !seen.has(keyFor(it)))
      .sort(byDateAsc);
    addList(filler);
  }

  return chosen.sort(byDateAsc);
}

// ================= helpers =================
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// timezone-safe date formatter
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return dt.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
  const dt = new Date(s);
  return isNaN(dt)
    ? s
    : dt.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
};
const ymdFromIso = (iso) =>
  (String(iso).match(/^(\d{4}-\d{2}-\d{2})/) || [, ''])[1] || '';

const toYMD = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse many date formats → YYYY-MM-DD (first day for ranges)
function toISODateSafe(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // M/D/YYYY
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mm = Number(m[1]),
      dd = Number(m[2]),
      yy = Number(m[3]);
    const d = new Date(yy, mm - 1, dd);
    return isNaN(d) ? null : toYMD(d);
  }

  // "Oct 2, 2024"
  m = s.match(/^([A-Za-z.]+)\s(\d{1,2}),\s*(\d{4})$/);
  if (m) {
    const MONTHS = {
      january: 0,
      jan: 0,
      february: 1,
      feb: 1,
      march: 2,
      mar: 2,
      april: 3,
      apr: 3,
      may: 4,
      june: 5,
      jun: 5,
      july: 6,
      jul: 6,
      august: 7,
      aug: 7,
      september: 8,
      sep: 8,
      sept: 8,
      october: 9,
      oct: 9,
      november: 10,
      nov: 10,
      december: 11,
      dec: 11
    };
    const key = m[1].toLowerCase().replace(/\.$/, '');
    const mi = MONTHS[key];
    const dt = new Date(Number(m[3]), mi, Number(m[2]));
    return mi == null || isNaN(dt) ? null : toYMD(dt);
  }

  // "Oct 2–8, 2024" or "Oct 31–Nov 3, 2024" → first day
  m = s.match(
    /^([A-Za-z.]+)\s(\d{1,2})\s*[-–]\s*([A-Za-z.]+)?\s*(\d{1,2}),\s*(\d{4})$/
  );
  if (m) return toISODateSafe(`${m[1]} ${m[2]}, ${m[5]}`);

  const dflt = new Date(s);
  return isNaN(dflt) ? null : toYMD(dflt);
}

/* === Compact upcoming-weeks bar chart (numbers outside) === */
const UpcomingLoadChart = ({ items, weeks = 8 }) => {
  const today = startOfDay(new Date());
  const data = Array.from({ length: weeks }, (_, i) => {
    const start = addDays(today, i * 7);
    const count = items.reduce((acc, it) => {
      const iso =
        toISODateSafe(it.date || it.dateText || it.text || it.event) || '';
      if (!iso) return acc;
      const d = new Date(iso + 'T00:00:00');
      return d >= start && d < addDays(start, 7) ? acc + 1 : acc;
    }, 0);
    return {
      label: `${start.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      })} – ${addDays(start, 6).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      })}`,
      count
    };
  });
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {data.map((d, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr max-content',
            gap: 12,
            alignItems: 'center'
          }}
        >
          <div className="week-label" style={{ fontSize: 13.5 }}>
            {d.label}
          </div>
          <div
            className="week-bar-remaining"
            style={{ position: 'relative', height: 12, borderRadius: 999 }}
          >
            <div
              title={`${d.count} deadline${d.count === 1 ? '' : 's'}`}
              style={{
                width: `${(d.count / max) * 100}%`,
                height: '100%',
                borderRadius: 999,
                background: '#4f46e5',
                transition: 'width 200ms ease'
              }}
            />
          </div>
          <div
            style={{
              fontSize: 13.5,
              color: '#e5e7eb',
              textAlign: 'right'
            }}
          >
            {d.count}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ===== reusable styles ===== */
/* 🔹 Darker blue date badge with white text */
const DATE_BADGE_STYLE = {
  fontSize: 16.5,
  fontWeight: 700,
  background: '#1d4ed8', // darker blue
  color: '#ffffff', // white text
  border: '1px solid #1e40af',
  padding: '7px 12px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
  minWidth: 118,
  textAlign: 'center'
};

// 🔹 Helper to detect Canvas items (by category or title)
const isCanvasItem = (item) => {
  const cat = (item?.category || '').toLowerCase();
  const title = (item?.event || item?.title || '').toLowerCase();
  return cat === 'canvas' || title.includes('canvas');
};

/* ===== campus resource helpers ===== */

/**
 * Try to infer campus from a deadline item.
 * Falls back to the user's campusPref if item has no explicit campus.
 */
function inferCampusKey(item, campusPref) {
  const raw =
    (item && (item.campus || item.campusCode || item.campus_name)) || '';
  const s = String(raw).toLowerCase();

  if (s.includes('bothell') || s === 'uwb') return 'uwb';
  if (s.includes('seattle') || s === 'uws') return 'uws';
  if (s.includes('tacoma') || s === 'uwt') return 'uwt';

  // fall back to preference if it's specific
  if (campusPref === 'uwb') return 'uwb';
  if (campusPref === 'uws') return 'uws';
  if (campusPref === 'uwt') return 'uwt';

  return null;
}

/**
 * Given a category + campus, return a suggested resource link.
 * You can update URLs later if you want exact pages.
 */
/**
 * Given a category + campus, return suggested resource links.
 * Returns: { description: string, links: [{ label, href }] } | null
 */
function getResourceLinkForItem(item, campusPref) {
  const category = (item?.category || '').toLowerCase();
  const title = (item?.event || item?.title || '').toLowerCase();
  const campusKey = inferCampusKey(item, campusPref);

  // Canvas deadlines: always Canvas
  if (category === 'canvas' || title.includes('canvas')) {
    return {
      description: 'Open Canvas to view or manage this assignment.',
      links: [
        {
          label: 'Open Canvas',
          href: 'https://canvas.uw.edu/'
        }
      ]
    };
  }

  // Personal events → no automatic resource
  if (category === 'personal') return null;

  // Helper for MyPlan/MyUW combos
  const regLinks = [
    { label: 'MyPlan', href: 'https://myplan.uw.edu/' },
    { label: 'MyUW', href: 'https://my.uw.edu/' }
  ];

  // === Add/Drop ===
  if (category === 'add/drop' || title.includes('add/drop')) {
    // All campuses use MyPlan for add/drop planning
    return {
      description: 'Use MyPlan to adjust your schedule around add/drop deadlines.',
      links: [{ label: 'MyPlan', href: 'https://myplan.uw.edu/' }]
    };
  }

  // === Financial Aid ===
  if (category === 'financial-aid' || title.includes('financial')) {
    if (campusKey === 'uwb') {
      return {
        description: 'Financial aid information for UW Bothell.',
        links: [
          {
            label: 'UW Bothell Financial Aid',
            href: 'https://www.uwb.edu/financial-aid/'
          }
        ]
      };
    }
    if (campusKey === 'uwt') {
      return {
        description: 'Financial aid information for UW Tacoma.',
        links: [
          {
            label: 'UW Tacoma Financial Aid',
            href: 'https://www.tacoma.uw.edu/finaid'
          }
        ]
      };
    }
    // Default / UW Seattle
    return {
      description: 'Financial aid information for UW Seattle.',
      links: [
        {
          label: 'UW Seattle Financial Aid',
          href: 'https://www.washington.edu/financialaid/applying-for-aid/'
        }
      ]
    };
  }

  // === Registration ===
  if (category === 'registration' || title.includes('registration')) {
    // All campuses: MyPlan + MyUW
    return {
      description:
        'Use MyPlan to plan your courses and MyUW to manage your registration and schedule.',
      links: regLinks
    };
  }

  // === Academic / Advising ===
  if (category === 'academic' || title.includes('quarter')) {
    if (campusKey === 'uwb') {
      return {
        description: 'Academic advising and planning resources for UW Bothell.',
        links: [
          {
            label: 'UW Bothell Advising',
            href: 'https://www.uwb.edu/advising/'
          }
        ]
      };
    }
    if (campusKey === 'uwt') {
      return {
        description: 'Academic advising and planning resources for UW Tacoma.',
        links: [
          {
            label: 'UW Tacoma Advising',
            href: 'https://www.tacoma.uw.edu/advising'
          }
        ]
      };
    }
    // Default / UW Seattle
    return {
      description: 'Academic advising and planning resources for UW Seattle.',
      links: [
        {
          label: 'UW Seattle Advising',
          href: 'https://advising.uw.edu/'
        }
      ]
    };
  }

  // Fallback: nothing special
  return null;
}

/* ================= main component ================= */
const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  function handleLogout() {
    localStorage.removeItem('token');
    setNavOpen(false);
    navigate('/login');
  }

const [groupedView, setGroupedView] = useState(false);
const [rangeFilter, setRangeFilter] = useState('upcoming'); // 'today' | 'tomorrow' | 'week' | 'upcoming'
  const [searchTerm, setSearchTerm] = useState('');

  const [miniSelectedDate, setMiniSelectedDate] = useState(null);
  const [miniSelectedItems, setMiniSelectedItems] = useState([]);

  const [userEmail, setUserEmail] = useState('');
  const [deadlines, setDeadlines] = useState([]);
  const [categoryFilters, setCategoryFilters] = useState({});
  const [campusPref, setCampusPref] = useState('both'); // 'uwb' | 'uws' | 'uwt' | 'both'

  const [userEvents, setUserEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const [ignoredAlerts, setIgnoredAlerts] = useState(new Set());
  const [fadingAlerts, setFadingAlerts] = useState(new Set());

  const [pinnedKeys, setPinnedKeys] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('pinnedKeys') || '[]'));
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem('pinnedKeys', JSON.stringify([...pinnedKeys]));
  }, [pinnedKeys]);

  const listRef = useRef(null);

  // Add-event modal state
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: '',
    endDate: '',
    category: 'personal',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Detail popup state
  const [detail, setDetail] = useState(null); // { type: 'scraped'|'personal', item: {...} }

  const keyForScraped = (item) => {
    const iso =
      toISODateSafe(item.date || item.dateText || item.text || item.event) ||
      '';
    const title = (item.event || item.title || '')
      .toLowerCase()
      .slice(0, 80);
    return `scr|${iso}|${title}`;
  };
  const keyForPersonal = (evt) => `me|${evt._id}`;

  useEffect(() => {
    if (!userEmail) return;
    (async () => {
      const r = await fetch(
        `${API}/api/pins?email=${encodeURIComponent(userEmail)}`
      );
      const data = await r.json();
      if (r.ok && Array.isArray(data.pins)) {
        setPinnedKeys(new Set(data.pins.map((p) => p.key)));
      }
    })();
  }, [userEmail]);

  // Load campus preference to show in the header badge
  useEffect(() => {
    if (!userEmail) return;

    (async () => {
      try {
        const res = await fetch(
          `${API}/api/preferences/${encodeURIComponent(userEmail)}`
        );
        if (!res.ok) return;
        const pref = await res.json();
        if (pref && typeof pref.campus_preference === 'string') {
          setCampusPref(pref.campus_preference);
        }
      } catch (e) {
        console.error('[Dashboard] failed to load campus preference:', e);
      }
    })();
  }, [userEmail]);

  // load scraped deadlines + user email
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded?.email) setUserEmail(decoded.email);
      } catch (e) {
        console.error('[Dashboard] jwtDecode failed:', e);
      }
    }

    const url = `${API}/api/deadlines`;
    console.log('[Dashboard] fetching deadlines from', url);

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    fetch(url, { headers })
      .then(async (r) => {
        console.log('[Dashboard] /api/deadlines status:', r.status);
        const text = await r.text();
        console.log('[Dashboard] raw response text:', text.slice(0, 200));

        let json;
        try {
          json = JSON.parse(text);
        } catch (e) {
          console.error('[Dashboard] JSON parse failed:', e);
          return setDeadlines([]);
        }

        console.log('[Dashboard] parsed JSON:', json);
        setDeadlines(Array.isArray(json) ? json : []);
      })
      .catch((err) => {
        console.error(
          '[Dashboard] FETCH FAILED (likely CORS or network):',
          err
        );
        setDeadlines([]);
      });
  }, []);

  async function refreshUserEvents(email) {
    if (!email) return;
    setLoadingEvents(true);
    try {
      const list = await fetch(
        `${API}/api/events?email=${encodeURIComponent(email)}`
      ).then((r) => (r.ok ? r.json() : []));
      const arr = Array.isArray(list) ? list : [];
      setUserEvents(arr);
      setPinnedKeys((prev) => {
        const next = new Set(prev);
        arr.forEach((e) => next.add(keyForPersonal(e)));
        return next;
      });
    } catch {
      setUserEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }

  async function togglePinOnServer(payload) {
    if (!userEmail) return alert('Please log in first.');

    const res = await fetch(`${API}/api/pins/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: userEmail, ...payload })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || 'Failed to toggle pin');

    setPinnedKeys(new Set((data.pins || []).map((p) => p.key)));
    return data.pins;
  }

  useEffect(() => {
    if (userEmail) refreshUserEvents(userEmail);
  }, [userEmail]);

  useEffect(() => {
    if (!deadlines.length) return;
    const present = new Set(deadlines.map((d) => d.category || 'other'));
    setCategoryFilters((prev) => {
      const next = { ...prev };
      present.forEach((c) => {
        if (!(c in next)) next[c] = true;
      });
      Object.keys(next).forEach((k) => {
        if (!present.has(k)) delete next[k];
      });
      return next;
    });
  }, [deadlines]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const today = new Date();
  const todayStart = startOfDay(today);

  const allowedFiltered = deadlines.filter((item) => {
    const category = item.category || 'other';
    const allowed = categoryFilters[category] ?? true;
    const matches =
      !searchTerm ||
      (item.event || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return allowed && matches;
  });

  const getDate = (it) => {
    const iso = toISODateSafe(it.date || it.dateText || it.text || it.event);
    return iso ? new Date(iso + 'T00:00:00') : new Date('Invalid');
  };

  const allAllowedSorted = allowedFiltered
    .map((d) => ({ d, t: getDate(d) }))
    .filter((x) => !isNaN(x.t))
    .sort((a, b) => a.t - b.t)
    .map((x) => x.d);

  const firstUpcomingIdx = allAllowedSorted.findIndex(
    (it) => getDate(it) >= todayStart
  );

  // Apply chips + includePast
const filteredDeadlines = allAllowedSorted.filter((it) => {
  const iso = toISODateSafe(it.date || it.dateText || it.text || it.event);
  if (!iso) return false;
  const diff = daysUntil(iso); // days from today (0=today, 1=tomorrow, etc.)

  // Always hide past deadlines
  if (diff < 0) return false;

  switch (rangeFilter) {
    case 'today':
      return diff === 0;
    case 'tomorrow':
      return diff === 1;
    case 'week':
      return diff >= 0 && diff <= 6;
    case 'upcoming':
    default:
      // All upcoming = today and future
      return diff >= 0;
  }
});



  const grouped = filteredDeadlines.reduce((acc, item) => {
    const cat = (item.category || 'other').toLowerCase();
    (acc[cat] ||= []).push(item);
    return acc;
  }, {});

  const sortedDeadlines = [...filteredDeadlines].sort((a, b) => {
    const ka = keyForScraped(a),
      kb = keyForScraped(b);
    const pa = pinnedKeys.has(ka) ? 1 : 0,
      pb = pinnedKeys.has(kb) ? 1 : 0;
    return pb - pa || getDate(a) - getDate(b);
  });

  const pinnedPersonal = useMemo(() => {
    return (userEvents || []).map((e) => ({
      _key: keyForPersonal(e),
      _source: 'personal',
      _id: e._id,
      event: e.title,
      date: ymdFromIso(e.start),
      category: e.category || 'personal',
      notes: e.notes || '',
      campus: e.campus || null,
      _raw: e
    }));
  }, [userEvents]);

  const pinnedScraped = useMemo(() => {
    const set = new Set([...pinnedKeys].filter((k) => k.startsWith('scr|')));
    if (!set.size) return [];
    return allAllowedSorted
      .map((it) => ({ it, k: keyForScraped(it) }))
      .filter((x) => set.has(x.k))
      .map((x) => ({
        _key: x.k,
        _source: 'scraped',
        event: x.it.event,
        date: toISODateSafe(
          x.it.date || x.it.dateText || x.it.text || x.it.event
        ),
        category: x.it.category || 'other',
        campus: x.it.campus || null,
        _raw: x.it
      }));
  }, [allAllowedSorted, pinnedKeys]);

  const pinnedItems = [...pinnedPersonal, ...pinnedScraped];

  const pinnedScrKeySet = useMemo(
    () => new Set([...pinnedKeys].filter((k) => k.startsWith('scr|'))),
    [pinnedKeys]
  );

  const pinnedCategorySet = useMemo(() => {
    const set = new Set();
    pinnedScraped.forEach((p) =>
      set.add((p.category || 'other').toLowerCase())
    );
    pinnedPersonal.forEach((p) => {
      const c = (p.category || '').toLowerCase();
      if (c && c !== 'personal') set.add(c);
    });
    return set;
  }, [pinnedScraped, pinnedPersonal]);

  const recommended = useMemo(() => {
    return computeRecommendedDeadlines({
      allItems: filteredDeadlines,
      pinnedCats: pinnedCategorySet,
      excludeKeys: pinnedScrKeySet,
      target: RECO_TARGET
    });
  }, [filteredDeadlines, pinnedCategorySet, pinnedScrKeySet]);

  const dayAgg = useMemo(() => {
    const agg = {};
    deadlines.forEach((it) => {
      const iso =
        toISODateSafe(it.date || it.dateText || it.text || it.event) || '';
      if (!iso) return;
      (agg[iso] ||= { count: 0, items: [] });
      agg[iso].count += 1;
      agg[iso].items.push({
        event: it.event,
        category: it.category,
        date: iso,
        campus: it.campus || null,
        _source: 'scraped'
      });
    });
    userEvents.forEach((e) => {
      const iso = toYMD(new Date(e.start));
      (agg[iso] ||= { count: 0, items: [] });
      agg[iso].count += 1;
      agg[iso].items.push({
        event: e.title,
        category: e.category || 'personal',
        date: iso,
        campus: e.campus || null,
        notes: e.notes || '',
        _source: 'personal'
      });
    });
    return agg;
  }, [deadlines, userEvents]);

  const campusLabel =
    campusPref === 'uwb'
      ? 'UW Bothell'
      : campusPref === 'uws'
      ? 'UW Seattle'
      : campusPref === 'uwt'
      ? 'UW Tacoma'
      : 'UW Bothell + UW Seattle + UW Tacoma';



  /* ===== layout ===== */
  const pageStyle = {
    padding: '1.25rem',
    minHeight: '100vh',
    background: 'var(--page-bg)'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 280px) 1fr',
    gap: '1.25rem',
    alignItems: 'start'
  };

  const card = {
    background: 'var(--widget-bg)',
    border: '1px solid rgba(148,163,184,0.4)',
    borderRadius: 12,
    padding: '1rem'
  };

// Campus-specific contact sets.
const CAMPUS_CONTACTS = {
  // UW Bothell contacts (you said these are fine as-is)
  uwb: [
    {
      name: 'Financial Aid (Bothell)',
      phone: '425-352-5240',
      email: 'uwbfaid@uw.edu'
      // website left as-is or null; user said Bothell contacts are fine
    },
    {
      name: 'Registration (Bothell)',
      phone: '425-352-5000',
      email: 'uwbreg@uw.edu'
    },
    {
      name: 'Admissions (Bothell)',
      phone: '425-352-5000',
      email: 'uwbinfo@uw.edu'
    },
    {
      name: 'Academic Advising (Bothell)',
      phone: null,
      email: 'uwbadvis@uw.edu',
      url: 'https://www.uwb.edu/advising/'
    }
  ],

  // UW Seattle contacts – now aligned with your URLs
  uws: [
    {
      name: 'Academic Advising (Seattle)',
      phone: null,
      email: null,
      url: 'https://advising.uw.edu/'
    },
    {
      name: 'Registration – MyPlan',
      phone: null,
      email: null,
      url: 'https://myplan.uw.edu/'
    },
    {
      name: 'Registration – MyUW',
      phone: null,
      email: null,
      url: 'https://my.uw.edu/'
    },
    {
      name: 'Financial Aid (Seattle)',
      phone: null,
      email: null,
      url: 'https://www.washington.edu/financialaid/applying-for-aid/'
    }
  ],

  // UW Tacoma contacts – now aligned with your URLs
  uwt: [
    {
      name: 'Academic Advising (Tacoma)',
      phone: null,
      email: null,
      url: 'https://www.tacoma.uw.edu/advising'
    },
    {
      name: 'Registration – MyPlan (Tacoma)',
      phone: null,
      email: null,
      url: 'https://myplan.uw.edu/'
    },
    {
      name: 'Registration – MyUW (Tacoma)',
      phone: null,
      email: null,
      url: 'https://my.uw.edu/'
    },
    {
      name: 'Financial Aid (Tacoma)',
      phone: null,
      email: null,
      url: 'https://www.tacoma.uw.edu/finaid'
    }
  ]
};


  const telHref = (s) => `tel:${String(s).replace(/[^\d+]/g, '')}`;

  const ContactsCard = ({ campusPref }) => {
    const BTN = {
      padding: '10px 14px',
      border: '1px solid rgba(148,163,184,0.5)',
      borderRadius: 10,
      background: 'var(--widget-bg)',
      textDecoration: 'none',
      fontSize: 15,
      fontWeight: 600,
      cursor: 'pointer'
    };

    // Normalize campus key
    const campusKey =
      campusPref === 'uwb'
        ? 'uwb'
        : campusPref === 'uws'
        ? 'uws'
        : campusPref === 'uwt'
        ? 'uwt'
        : 'both';

    // Build sections to render
    let sections;
    if (campusKey === 'both') {
      sections = [
        { label: 'UW Bothell', contacts: CAMPUS_CONTACTS.uwb || [] },
        { label: 'UW Seattle', contacts: CAMPUS_CONTACTS.uws || [] },
        { label: 'UW Tacoma', contacts: CAMPUS_CONTACTS.uwt || [] }
      ].filter((sec) => sec.contacts && sec.contacts.length);
    } else {
      const label =
        campusKey === 'uwb'
          ? 'UW Bothell'
          : campusKey === 'uws'
          ? 'UW Seattle'
          : campusKey === 'uwt'
          ? 'UW Tacoma'
          : 'UW Campus';

      sections = [
        {
          label,
          contacts: CAMPUS_CONTACTS[campusKey] || []
        }
      ];
    }

    return (
      <div style={card}>
        <h3 style={{ margin: '0 0 .25rem 0', fontSize: '1.3rem' }}>
          Campus Contacts
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: '#6b7280'
          }}
        >
          Showing offices for your campus preference.
        </p>

        <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
          {sections.map((section, idx) => (
            <div key={section.label + idx}>
              {sections.length > 1 && (
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#6b7280',
                    marginBottom: 6
                  }}
                >
                  {section.label}
                </div>
              )}

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gap: 12
                }}
              >
                {section.contacts.map((c, i) => (
                  <li
                    key={`${section.label}-${i}`}
                    style={{
                      border: '1px solid rgba(148,163,184,0.4)',
                      borderRadius: 12,
                      padding: 16,
                      background: 'var(--widget-sub-bg)'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 8
                      }}
                    >
                      <strong style={{ fontSize: 17 }}>{c.name}</strong>
                      {c.url && (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            fontSize: 13,
                            color: '#60a5fa',
                            textDecoration: 'none'
                          }}
                        >
                          
                        </a>
                      )}
                    </div>

                    <div
                      className="contact-info"
                      style={{
                        fontSize: 14,
                        marginTop: 8,
                        display: 'grid',
                        gap: 4,
                        lineHeight: 1.45
                      }}
                    >
                      {c.phone && (
                        <div>
                          <span>Phone: </span>
                          <a href={telHref(c.phone)}>{c.phone}</a>
                        </div>
                      )}
                      {c.email && (
                        <div>
                          <span>Email: </span>
                          <a href={`mailto:${c.email}`}>{c.email}</a>
                        </div>
                      )}
                    </div>

                    <div
                      style={{ display: 'flex', gap: 10, marginTop: 10 }}
                    >
                      {c.phone && (
                        <a href={telHref(c.phone)} style={BTN}>
                          Call
                        </a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} style={BTN}>
                          Email
                        </a>
                      )}
                      {c.url && (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          style={BTN}
                        >
                          Website
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const togglePinKeyLocal = (k) =>
    setPinnedKeys((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const deletePersonal = async (mongoId, key) => {
    if (!mongoId) return;
    if (!window.confirm('Delete this personal event?')) return;
    try {
      await fetch(`${API}/api/events/${mongoId}`, { method: 'DELETE' });
      await refreshUserEvents(userEmail);
      setPinnedKeys((prev) => {
        const n = new Set(prev);
        n.delete(key);
        return n;
      });
    } catch {
      alert('Failed to delete.');
    }
  };

  async function syncPinsToServer(email, pinsPayload) {
    if (!email) return;
    const res = await fetch(`${API}/api/pins/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, pins: pinsPayload })
    });
    try {
      const data = await res.json();
      if (res.ok && Array.isArray(data.pins)) {
        setPinnedKeys(new Set(data.pins.map((p) => p.key)));
      }
    } catch {}
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!userEmail) return alert('Please log in first.');
    if (!form.title.trim() || !form.date) {
      return alert('Title and start date are required.');
    }

    const startDate = new Date(form.date + 'T00:00:00');
    const endDate = form.endDate
      ? new Date(form.endDate + 'T00:00:00')
      : new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + 1
        );

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          title: form.title.trim(),
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          category: form.category,
          notes: form.notes
        })
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
      if (!res.ok) {
        alert(`Failed to save: ${data?.message || text || res.status}`);
        return;
      }

      await refreshUserEvents(userEmail);

      setAddOpen(false);
      setForm({
        title: '',
        date: '',
        endDate: '',
        category: 'personal',
        notes: ''
      });
      alert('Saved!');
    } catch (err) {
      alert(`Failed to save: ${String(err?.message || err)}`);
    } finally {
      setSaving(false);
    }
  }

  // Detail popup helpers
  const openDetailFromScraped = (item) => {
    setDetail({
      type: 'scraped',
      item
    });
  };

  const openDetailFromPinned = (pItem) => {
    // pItem has _source and possibly _raw
    if (pItem._source === 'personal' && pItem._raw) {
      setDetail({ type: 'personal', item: pItem._raw });
    } else if (pItem._source === 'scraped' && pItem._raw) {
      setDetail({ type: 'scraped', item: pItem._raw });
    } else {
      // fallback: treat as scraped-ish
      setDetail({ type: pItem._source || 'scraped', item: pItem });
    }
  };

  const closeDetail = () => setDetail(null);

  // NOTE: now accepts onAddEvent
  const RecommendedCard = ({ items, onAddEvent }) => {
    const keyFor = (it) =>
      `scr|${getISOFromItem(it) || ''}|${(it.event || it.title || '')
        .toLowerCase()
        .slice(0, 80)}`;

    const [rows, setRows] = useState(items);
    const [exiting, setExiting] = useState(new Set());
    const [entering, setEntering] = useState(new Set());

    useEffect(() => {
      const prevKeys = rows.map(keyFor);
      const nextKeys = items.map(keyFor);

      const toAdd = items.filter((it) => !prevKeys.includes(keyFor(it)));
      const toRemove = prevKeys.filter((k) => !nextKeys.includes(k));

      if (!toAdd.length && !toRemove.length) return;

      setRows((prev) => {
        const map = new Map(prev.map((it) => [keyFor(it), it]));
        toAdd.forEach((it) => map.set(keyFor(it), it));
        const ordered = [
          ...items,
          ...[...map.values()].filter((it) => !nextKeys.includes(keyFor(it)))
        ];
        return ordered;
      });

      if (toAdd.length) {
        setEntering((prev) => {
          const next = new Set(prev);
          toAdd.forEach((it) => next.add(keyFor(it)));
          return next;
        });
        setTimeout(() => {
          setEntering((prev) => {
            const next = new Set(prev);
            toAdd.forEach((it) => next.delete(keyFor(it)));
            return next;
          });
        }, 300);
      }

      if (toRemove.length) {
        setExiting((prev) => {
          const next = new Set(prev);
          toRemove.forEach((k) => next.add(k));
          return next;
        });
        setTimeout(() => {
          setRows((cur) => cur.filter((it) => !toRemove.includes(keyFor(it))));
          setExiting((prev) => {
            const next = new Set(prev);
            toRemove.forEach((k) => next.delete(k));
            return next;
          });
        }, 250);
      }
    }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div style={card}>
        <style>{`
          .reco-item { transition: opacity .25s ease, transform .25s ease; }
          .reco-enter { opacity: 0; transform: translateY(4px); }
          .reco-exit  { opacity: 0; transform: translateY(-4px); }
        `}</style>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h3 style={{ marginTop: 0 }}>Suggested Deadlines</h3>
          {onAddEvent && (
            <button
              type="button"
              onClick={onAddEvent}
              style={{
                padding: '0.35rem 0.6rem',
                border: '1px solid #6a6a6a',
                background: 'var(--widget-bg)',
                borderRadius: 6,
                cursor: 'pointer'
              }}
              title="Add a personal event"
            >
              + Add Event
            </button>
          )}
        </div>

        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {rows.length > 0 ? (
            <ul
              style={{
                paddingLeft: 0,
                listStyle: 'none',
                margin: 0,
                display: 'grid',
                gap: 8
              }}
            >
              {rows.map((item, i) => {
                const k = keyFor(item);
                const isPinned = pinnedKeys.has(k);
                const cls = [
                  'reco-item',
                  entering.has(k) ? 'reco-enter' : '',
                  exiting.has(k) ? 'reco-exit' : ''
                ]
                  .join(' ')
                  .trim();

                const canvasStripe = isCanvasItem(item)
                  ? { boxShadow: '0 -3px 0 #8b5cf6, 0 3px 0 #8b5cf6' }
                  : {};

                return (
                  <li
                    key={`reco-${k}-${i}`}
                    className={`${cls} deadline-row ${urgencyClass(
                      item.date
                    )}`}
                    style={{
                      padding: '1rem 12px',
                      borderTop: '1px solid #4b5563',
                      display: 'flex',
                      gap: '.75rem',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      ...canvasStripe
                    }}
                    onClick={() => openDetailFromScraped(item)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        minWidth: 0,
                        flex: 1
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            minWidth: 0
                          }}
                        >
                          <strong
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item.event || 'Untitled'}
                          </strong>
                          <span
                            className="deadline-chip"
                            style={{
                              textTransform: 'capitalize',
                              border: isCanvasItem(item)
                                ? '1px solid #8b5cf6'
                                : undefined
                            }}
                          >
                            {item.category || 'other'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={DATE_BADGE_STYLE}>
                        {fmtDate(item.date)}
                      </span>
                      <button
                        type="button"
                        disabled={!userEmail}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePinOnServer(buildScrapedPayload(item));
                        }}
                        className="pin-btn"
                        title={isPinned ? 'Unpin' : 'Pin'}
                      >
                        {isPinned ? '★ Unpin' : '☆ Pin'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div>Nothing to recommend right now.</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={pageStyle}>
      <style>{`
        .fc-mini .fc-daygrid-day-number {
          display:block; width:100%; text-align:center;
          font-weight:700; color:#111; padding:0; margin:0;
        }
        .fc-mini a.fc-count-link {
          text-decoration: underline;
          color: #1d4ed8;
          font-weight: 700;
          cursor: pointer;
        }
        .fc-mini a.fc-count-link:focus {
          outline: 2px solid #2563eb;
          outline-offset: 2px;
          border-radius: 4px;
        }  
        .deadline-row {
          --bg: transparent;
          --ring: transparent;
          background: var(--bg);
          border-left: 4px solid var(--ring);
          border-radius: 8px;
          transition: background-color .12s ease, box-shadow .12s ease;
        }

        /* Light blue hover in light mode */
        .deadline-row:hover {
          background: #e0f2fe;
        }

        /* Deep slate hover in dark mode */
        [data-theme="dark"] .deadline-row:hover {
          background: #1f2937;
        }

        .deadline-row:focus-within {
          box-shadow: 0 0 0 2px #60a5fa inset;
        }

        .deadline-row.u-1w    { --bg: rgba(239, 68,  68, .08);  --ring: #f87171; }
        .deadline-row.u-2w    { --bg: rgba(245, 158, 11, .10);  --ring: #f59e0b; }
        .deadline-row.u-later { --bg: rgba( 16, 185,129, .08);  --ring: #34d399; }
        .deadline-row.u-past  { --bg: rgba(107, 114,128, .06);  --ring: #9ca3af; }

        .modal-backdrop,
        .detail-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          z-index: 40;
        }

        .modal-card,
        .detail-card {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 41;
          pointer-events: none;
        }

        .modal-inner,
        .detail-inner {
          pointer-events: auto;
          max-width: 480px;
          width: 100%;
          margin: 1.5rem;
          background: var(--widget-bg, #020617);
          border-radius: 12px;
          border: 1px solid rgba(148,163,184,0.55);
          padding: 1.25rem 1.5rem;
          box-shadow: 0 18px 45px rgba(15,23,42,0.6);
          animation: fadeInScale .16s ease-out;
        }

        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          paddingBottom: 4,
          borderBottom: '1px solid rgba(148,163,184,0.35)'
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
        >
          <h1 style={{ margin: 0 }}>Student Dashboard</h1>
          <span
            style={{
              fontSize: 12,
              padding: '4px 10px',
              borderRadius: 999,
              background: '#eef2ff',
              color: '#3730a3',
              border: '1px solid #c7d2fe',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em'
            }}
          >
            {campusLabel}
          </span>
        </div>
        <p style={{ marginTop: 4 }}>
          Welcome, <strong>{userEmail || '...'}</strong>
        </p>
      </div>

      <div
        className={`drawer ${navOpen ? 'open' : ''}`}
        aria-hidden={!navOpen}
      >
        <div
          className="backdrop"
          onClick={() => setNavOpen(false)}
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
        />
        <aside
          className="panel"
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div style={gridStyle}>
        {/* LEFT */}
        <aside
          style={{
            display: 'grid',
            gap: '1rem',
            fontSize: 15,
            lineHeight: 1.35
          }}
        >
          <ContactsCard campusPref={campusPref} />
        </aside>

        {/* RIGHT */}
        <main style={{ display: 'grid', gap: '1rem' }}>
          {/* Quick range chips */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center'
              }}
            >
              <span
                style={{ fontSize: 13, color: '#6b7280' }}
              >
                Focus range:
              </span>
{[
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'Next 7 days' },
  { id: 'upcoming', label: 'All upcoming' }
].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setRangeFilter(opt.id)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 999,
                    border:
                      rangeFilter === opt.id
                        ? '1px solid #4f46e5'
                        : '1px solid rgba(148,163,184,0.6)',
                    background:
                      rangeFilter === opt.id
                        ? 'rgba(79,70,229,0.1)'
                        : 'transparent',
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>


          </div>

          {/* Upcoming 4 Weeks */}
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Upcoming 4 Weeks</h3>
            <UpcomingLoadChart items={filteredDeadlines} weeks={4} />
          </div>

          {/* Pinned (personal auto + any pinned scraped) */}
          {pinnedItems.length > 0 && (
            <div style={card}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <h3 style={{ marginTop: 0 }}>Pinned Deadlines</h3>
                <button
                  onClick={() => setAddOpen(true)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    border: '1px solid #6a6a6a',
                    background: 'var(--widget-bg)',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                  title="Add a personal event"
                >
                  + Add Event
                </button>
              </div>
              <ul
                style={{
                  paddingLeft: 0,
                  listStyle: 'none',
                  margin: 0,
                  display: 'grid',
                  gap: 8
                }}
              >
                {pinnedItems.map((item, i) => {
                  const isPersonal = item._source === 'personal';
                  const canvasStripe =
                    !isPersonal && isCanvasItem(item)
                      ? {
                          boxShadow:
                            '0 -3px 0 #8b5cf6, 0 3px 0 #8b5cf6'
                        }
                      : {};

                  return (
                    <li
                      key={`pin-${item._key}-${i}`}
                      className={`deadline-row ${urgencyClass(
                        item.date
                      )}`}
                      style={{
                        border: '1px solid #4b5563',
                        borderRadius: 8,
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        ...canvasStripe
                      }}
                      onClick={() => openDetailFromPinned(item)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.event}
                          {isPersonal && (
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: 11,
                                color: '#e5e7eb',
                                border: '1px solid #4b5563',
                                borderRadius: 999,
                                padding: '2px 6px'
                              }}
                            >
                              personal
                            </span>
                          )}
                        </div>
                        {!isPersonal && (
                          <div
                            style={{
                              fontSize: 12,
                              color: '#9ca3af',
                              textTransform: 'capitalize'
                            }}
                          >
                            {item.category || 'other'}
                          </div>
                        )}
                      </div>
                      <span style={DATE_BADGE_STYLE}>
                        {fmtDate(item.date)}
                      </span>
                      <div
                        style={{ display: 'flex', gap: 8 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isPersonal ? (
                          <button
                            type="button"
                            onClick={() =>
                              deletePersonal(item._id, item._key)
                            }
                            style={{
                              border: '1px solid #b91c1c',
                              padding: '0.25rem 0.5rem',
                              borderRadius: 6,
                              cursor: 'pointer',
                              background: '#7f1d1d',
                              color: '#fee2e2'
                            }}
                            title="Delete personal event"
                          >
                            🗑 Delete
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={!userEmail}
                            onClick={() =>
                              togglePinOnServer({ key: item._key })
                            }
                            className="pin-btn"
                            title="Unpin"
                          >
                            ★ Unpin
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {loadingEvents && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#9ca3af'
                  }}
                >
                  Loading personal events…
                </div>
              )}
            </div>
          )}

          {groupedView ? (
            <div style={card}>
              <h3 style={{ marginTop: 0 }}>Deadlines by Category</h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '12px',
                  marginTop: 8
                }}
              >
                {Object.keys(grouped)
                  .sort()
                  .map((cat) => (
                    <div
                      key={cat}
                      style={{
                        border: '1px solid rgba(148,163,184,0.4)',
                        borderRadius: 8,
                        padding: '12px',
                        background: 'var(--widget-bg)'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 8
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: '1.05rem',
                            textTransform: 'capitalize'
                          }}
                        >
                          {cat.replace('-', ' ')}
                        </h4>
                        <span
                          style={{
                            fontSize: 12,
                            color: '#9ca3af'
                          }}
                        >
                          {grouped[cat].length}
                        </span>
                      </div>
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          maxHeight: 320,
                          overflowY: 'auto'
                        }}
                      >
                        {grouped[cat].map((item, index) => {
                          const k = keyForScraped(item);
                          const isPinned = pinnedKeys.has(k);
                          const canvasStripe = isCanvasItem(item)
                            ? {
                                boxShadow:
                                  '0 -3px 0 #8b5cf6, 0 3px 0 #8b5cf6'
                              }
                            : {};

                          return (
                            <li
                              key={`${cat}-${index}`}
                              className={`deadline-row ${urgencyClass(
                                item.date
                              )}`}
                              style={{
                                padding: '8px 0',
                                borderTop: '1px solid #4b5563',
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                ...canvasStripe
                              }}
                              onClick={() => openDetailFromScraped(item)}
                            >
                              <div style={{ minWidth: 0 }}>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {item.event || 'Untitled'}
                                </span>
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: '#9ca3af',
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {item.category || 'other'}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span style={DATE_BADGE_STYLE}>
                                  {fmtDate(item.date)}
                                </span>
                                <button
                                  type="button"
                                  disabled={!userEmail}
                                  onClick={() =>
                                    togglePinOnServer(
                                      buildScrapedPayload(item)
                                    )
                                  }
                                  className="pin-btn"
                                  title={isPinned ? 'Unpin' : 'Pin'}
                                >
                                  {isPinned ? '★ Unpin' : '☆ Pin'}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <RecommendedCard
              items={recommended}
              onAddEvent={() => setAddOpen(true)}
            />
          )}
        </main>
      </div>

      {/* ==== ADD EVENT MODAL ==== */}
      {addOpen && (
        <>
          <div
            className="modal-backdrop"
            onClick={() => setAddOpen(false)}
          />
          <div className="modal-card">
            <div className="modal-inner">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 10
                }}
              >
                <h3 style={{ margin: 0 }}>Add Personal Event</h3>
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#9ca3af'
                  }}
                >
                  ×
                </button>
              </div>

              <form
                onSubmit={onSubmit}
                style={{
                  display: 'grid',
                  gap: 10,
                  marginTop: 4
                }}
              >
                <label style={{ fontSize: 14 }}>
                  Title
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        title: e.target.value
                      }))
                    }
                    required
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '6px 8px',
                      borderRadius: 6,
                      border:
                        '1px solid rgba(148,163,184,0.7)',
                      background: 'var(--widget-sub-bg, #020617)',
                      color: 'inherit'
                    }}
                  />
                </label>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8
                  }}
                >
                  <label style={{ fontSize: 14 }}>
                    Start date
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          date: e.target.value
                        }))
                      }
                      required
                      style={{
                        width: '100%',
                        marginTop: 4,
                        padding: '6px 8px',
                        borderRadius: 6,
                        border:
                          '1px solid rgba(148,163,184,0.7)',
                        background:
                          'var(--widget-sub-bg, #020617)',
                        color: 'inherit'
                      }}
                    />
                  </label>
                  <label style={{ fontSize: 14 }}>
                    End date (optional)
                    <input
                      type="date"
                      value={form.endDate}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          endDate: e.target.value
                        }))
                      }
                      style={{
                        width: '100%',
                        marginTop: 4,
                        padding: '6px 8px',
                        borderRadius: 6,
                        border:
                          '1px solid rgba(148,163,184,0.7)',
                        background:
                          'var(--widget-sub-bg, #020617)',
                        color: 'inherit'
                      }}
                    />
                  </label>
                </div>

                <label style={{ fontSize: 14 }}>
                  Category
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        category: e.target.value
                      }))
                    }
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '6px 8px',
                      borderRadius: 6,
                      border:
                        '1px solid rgba(148,163,184,0.7)',
                      background:
                        'var(--widget-sub-bg, #020617)',
                      color: 'inherit'
                    }}
                  >
                    <option value="personal">Personal</option>
                    <option value="academic">Academic</option>
                    <option value="registration">
                      Registration
                    </option>
                    <option value="financial-aid">
                      Financial Aid
                    </option>
                    <option value="canvas">Canvas</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label style={{ fontSize: 14 }}>
                  Notes (optional)
                  <textarea
                    value={form.notes}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        notes: e.target.value
                      }))
                    }
                    rows={3}
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '6px 8px',
                      borderRadius: 6,
                      border:
                        '1px solid rgba(148,163,184,0.7)',
                      background:
                        'var(--widget-sub-bg, #020617)',
                      color: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </label>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    marginTop: 8
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setAddOpen(false)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border:
                        '1px solid rgba(148,163,184,0.7)',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#4f46e5',
                      color: '#e5e7eb',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      opacity: saving ? 0.7 : 1
                    }}
                  >
                    {saving ? 'Saving…' : 'Save event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* ==== DEADLINE DETAIL POPUP ==== */}
      {detail && (
        <>
          <div
            className="detail-backdrop"
            onClick={closeDetail}
          />
          <div className="detail-card">
            <div className="detail-inner">
              {(() => {
                const item = detail.item || {};
                const category = (item.category || '').toLowerCase();
                const title = item.event || item.title || 'Untitled';
                const dateIso =
                  toISODateSafe(
                    item.date ||
                      item.dateText ||
                      item.text ||
                      item.start ||
                      item.event
                  ) || null;
                const notes =
                  item.notes ||
                  item.description ||
                  item.details ||
                  '';
                const resource = getResourceLinkForItem(
                  item,
                  campusPref
                );

                return (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: '#9ca3af',
                            marginBottom: 2
                          }}
                        >
                          {detail.type === 'personal'
                            ? 'Personal event'
                            : 'Deadline'}
                        </div>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: '1.1rem'
                          }}
                        >
                          {title}
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={closeDetail}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          fontSize: 20,
                          cursor: 'pointer',
                          color: '#9ca3af'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 10
                      }}
                    >
                      <span
                        style={{
                          textTransform: 'capitalize',
                          fontSize: 12,
                          padding: '3px 9px',
                          borderRadius: 999,
                          border: '1px solid rgba(148,163,184,0.7)',
                          background: isCanvasItem(item)
                            ? 'rgba(139,92,246,0.12)'
                            : 'transparent'
                        }}
                      >
                        {category || 'other'}
                      </span>
                      {dateIso && (
                        <span style={DATE_BADGE_STYLE}>
                          {fmtDate(dateIso)}
                        </span>
                      )}
                    </div>

                    {notes && (
                      <div
                        style={{
                          marginBottom: 10,
                          fontSize: 14,
                          whiteSpace: 'pre-wrap',
                          color: '#e5e7eb'
                        }}
                      >
                        {notes}
                      </div>
                    )}

{resource && (
  <div
    style={{
      marginTop: 6,
      paddingTop: 8,
      borderTop: '1px dashed rgba(148,163,184,0.6)'
    }}
  >
    <div
      style={{
        fontSize: 12,
        color: '#9ca3af',
        marginBottom: 4
      }}
    >
      Suggested resource{resource.links?.length > 1 ? 's' : ''}:
    </div>
    {resource.description && (
      <p
        style={{
          fontSize: 13,
          margin: '0 0 6px 0',
          color: '#111827'
        }}
      >
        {resource.description}
      </p>
    )}
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
      }}
    >
      {(resource.links || []).map((link, idx) => (
<a
  key={link.href + idx}
  href={link.href}
  target="_blank"
  rel="noreferrer"
  style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid #4f46e5',
    textDecoration: 'none',
    color: '#111827',                   // 🔹 dark, readable text
    background: 'rgba(79,70,229,0.16)', // you can keep this
    fontWeight: 600                     // optional: makes it pop a bit more
  }}
>
  {link.label} 
</a>

      ))}
    </div>
  </div>
)}


                    {!resource && (
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6b7280',
                          marginTop: 6
                        }}
                      >
                        No automatic resource link for this item.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
